import { useCallback, useRef, useState } from 'react'

export function useDebouncedSave(onSaveEdits: (content: string) => Promise<void>) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveInProgressRef = useRef(false)
  const pendingSaveRef = useRef<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const debouncedSave = useCallback(
    async (content: string) => {
      // Cancel any pending debounced save
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }

      // Schedule new save - don't disable during debounce, only during actual save
      debounceRef.current = setTimeout(async () => {
        debounceRef.current = null

        // If save already in progress, queue this content
        if (saveInProgressRef.current) {
          pendingSaveRef.current = content
          return
        }

        // Set disabled state when actual save starts
        setIsSaving(true)
        saveInProgressRef.current = true

        try {
          await onSaveEdits(content)

          // If another save was queued while we were saving, process it
          if (pendingSaveRef.current) {
            const nextContent = pendingSaveRef.current
            pendingSaveRef.current = null
            saveInProgressRef.current = false
            await debouncedSave(nextContent)
          } else {
            saveInProgressRef.current = false
            setIsSaving(false)
          }
        } catch (error) {
          saveInProgressRef.current = false
          pendingSaveRef.current = null
          setIsSaving(false)
          throw error
        }
      }, 2000)
    },
    [onSaveEdits],
  )

  return { debouncedSave, isSaving }
}
